// src/components/ViewBOM.jsx
import React, { useState, useEffect } from "react";
import BOMTableTemplate from "./BOMTableTemplate";
import AddEditBOM from "./AddEditBOM";
import { Button, Box, Drawer, Typography, CircularProgress } from "@mui/material";
import axios from "axios";
import env from "react-dotenv";

const ViewBOM = ({ productId, onViewChemicals }) => {
    const [data, setData] = useState([]);
    const [productNames, setProductNames] = useState({});
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedDrawer, setExpandedDrawer] = useState(false); // State for drawer expansion

    useEffect(() => {
        fetchData(productId);
        fetchProductNames();
    }, [productId]);

    const fetchData = async (id) => {
        try {
            const url = id 
                ? `${process.env.REACT_APP_PJCBACKEND_URL}/api/bom/product/${id}`
                : `${process.env.REACT_APP_PJCBACKEND_URL}/api/bom`;
            const response = await axios.get(url);
            setData(response.data);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Failed to fetch BOM data.");
        } finally {
            setLoading(false);
        }
    };

    const fetchProductNames = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`);
            const products = response.data;
            const productMap = {};
            products.forEach(product => {
                productMap[product.id] = product.product_name;
            });
            setProductNames(productMap);
        } catch (error) {
            console.error("Error fetching product names:", error);
        }
    };

    const handleAdd = () => {
        setEditData(null);
        setOpen(true);
        setExpandedDrawer(false); // Reset to not expanded when adding a new entry
    };

    const handleEdit = (item) => {
        setEditData(item);
        setOpen(true);
        setExpandedDrawer(false); // Reset to not expanded when editing
    };

    const handleDelete = async (bom_id) => {
        try {
            console.log("Deleting BOM with ID:", bom_id);
            await axios.delete(`${process.env.REACT_APP_PJCBACKEND_URL}/api/bom/${bom_id}`);
            setData(data.filter(item => item.bom_id !== bom_id));
        } catch (error) {
            console.error("Error deleting record:", error);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setExpandedDrawer(false); // Reset drawer expansion on close
    };

    const handleSave = async (item) => {
        try {
            // console.log("Saving BOM data:", item);
            
            // Create a new object with only the relevant fields for the BOM table
            const { product_name, new_product_name, new_RM_product_name, ...dataToSave } = item;
    
            // Create the final payload for the BOM table
            const finalDataToSave = {
                product_id: dataToSave.product_id,
                part_id: dataToSave.part_id,
                part_name: dataToSave.part_name,
                quantity_required: dataToSave.quantity_required,
                is_active: dataToSave.is_active,
                chemicals: dataToSave.chemicals // Make sure this aligns with how you structure data
                // Include any other fields that are relevant to the BOM table
            };
    
            if (item.bom_id) {
                await axios.put(`${process.env.REACT_APP_PJCBACKEND_URL}/api/bom/${item.bom_id}`, finalDataToSave);
            } else {
                await axios.post(`${process.env.REACT_APP_PJCBACKEND_URL}/api/bom`, finalDataToSave);
            }
            fetchData(productId);
        } catch (error) {
            console.error("Error saving record:", error);
        }
        setOpen(false);
        setExpandedDrawer(false); // Reset drawer expansion on save
    };

    const handleExpandDrawer = () => {
        setExpandedDrawer(true); // Set to true when expanding the drawer
    };


    const transformedData = data.map(item => {
        return {
            ...item,
            product_name: productNames[item.product_id] || item.product_id
        };
    });

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <Typography variant="h6" color="error">No records found</Typography>
            </Box>
        );
    }

    const handleViewChemicals = (bom_id, part_id) => {
        onViewChemicals(bom_id, part_id); // Call the passed function to open ViewBOMChemicals
    };

    return (
        <Box
        sx={{
            height: 670,
            width: '85%',
            marginLeft:"210px",
            marginRight:"150px",
          }}
        >
            <BOMTableTemplate
                columns={["product_name", "part_name", "quantity_required", "unit_of_measure", "cost_per_unit"]}
                headers={{
                    columns: ["Product Name", "Raw Material Name", "Quantity Required", "Unit of Measure", "Raw Material Cost"]
                }}
                iterate={transformedData}
                data={transformedData}
                onDelete={handleDelete} // Directly pass handleDelete
                onEdit={handleEdit}
                onAdd={handleAdd}
                onViewChemicals={handleViewChemicals} // This line is already present
                add
                edit
            />

            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                sx={{
                    "& .MuiDrawer-paper": { width: expandedDrawer ? "800px" : "600px" } // Expand when form is open
                }}
            >
                <AddEditBOM 
                    onClose={handleClose} 
                    onSave={handleSave} 
                    editData={editData} 
                    onExpandDrawer={handleExpandDrawer} // Pass the expand function to the child component 
                />
            </Drawer>
        </Box>
    );
};

export default ViewBOM;